/* eslint-disable react/prop-types */
import React, {
  useState,
  useEffect,
  useRef,
  createRef,
  useContext,
  useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Swiper from 'swiper';
import styled from 'styled-components';
import { ToastCtx } from '~/providers/ToastProvider';
import LockCard from '~/compositions/LockCard';
import UnlockCard from '~/compositions/UnlockCard';
import Maps from '~/compositions/Maps';
import NoCards from '~/pages/Home/NoCards';
import HelpButton from '~/elements/HelpButton';
import useMapsScript from '~/hooks/useMapsScript';
import SliderBullets from '../../elements/SliderBullets';
import { setActiveType, setCardsList } from '~/store/modules/home/actions';
import { setCreatingPassword } from '~/store/modules/cardPassword/actions';
import { cards as CardsApi } from '../../helpers/api';
import messages from '../../helpers/api/messages';
import { Transactions } from '../../compositions';
import { CardActions, Modal, Loader } from '../../components';
import ChangeCardPassword from '../../compositions/ChangeCardPassword';
import Header from '../../compositions/Header';
import CardDetails from './CardDetails';
import { Container, Wrapper } from './common';
import TermsAndPolicy from '~/compositions/TermsAndPolicy';
import ModalInactivationPortal from '~/compositions/ModalInactivationPortal';

const Body = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding-bottom: 34px;
  color: ${({ theme }) => theme.colors.gray400};
  padding-top: 157px;
  margin-top: -188px;

  .mobile-balance-management {
    display: none;
    &.blocked {
      width: 100%;
      max-width: 333px;
      margin: 20px auto 0;
    }
  }

  @media (max-width: ${({ theme }) => theme.devices.tablet}) {
    padding-top: 50px;
    padding-bottom: 72px;
    margin-top: -92px;
    min-height: ${({ minHeight }) => minHeight}px;
    .mobile-balance-management {
      display: block;
    }
  }
`;

const Home = () => {
  const { href } = window.location;
  const [activeSlide, setActiveSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [clearValues, setClearValues] = useState(false);
  const [swiper, setSwiper] = useState();
  const [clickedItem, setClickedItem] = useState('');
  const [showMaps, setShowMaps] = useState(false);
  const [documentHeight, setDocumentHeight] = useState(window.innerHeight);
  const body = useRef();
  const [bodyTop, setBodyTop] = useState();
  const swiperEl = createRef();
  const toast = useContext(ToastCtx);
  const { toastIsOpened } = toast;
  const user = useSelector(state => state.user);
  const cards = useSelector(state => state.home.cards.list);
  const dispatch = useDispatch();
  const hasContent = cards?.length > 0;
  useMapsScript();

  useEffect(() => {
    if (currentStep === '') {
      setModalIsOpen(false);
    }
  }, [currentStep]);

  useEffect(() => {
    if (cards && swiperEl && !swiper) {
      setSwiper(
        new Swiper('.swiper-container', {
          direction: 'horizontal',
          loop: false,
          allowTouchMove: false,
        }),
      );
    }
  }, [cards, swiper, swiperEl]);

  useEffect(() => {
    if (swiper) {
      swiper.slideTo(activeSlide);
    }
    // eslint-disable-next-line
  }, [activeSlide]);

  const getCards = useCallback(
    async occasion => {
      try {
        if (occasion === 'first_load') {
          setIsLoading(true);
        }
        const cardsData = (await CardsApi.getCards()) || [];

        dispatch(setCardsList(cardsData));
        if (cardsData.length) {
          dispatch(setActiveType(cardsData[0]));
        }
      } catch (e) {
        toast.dispatch(messages(e.response?.data || e, 'home'), 'error');
      } finally {
        if (occasion === 'first_load') {
          setIsLoading(false);
        }
        // eslint-disable-next-line
      }
    },
    [user],
  );

  useEffect(() => {
    if (!hasContent) {
      getCards('first_load');
    }

    window.addEventListener('resize', () => {
      setDocumentHeight(window.innerHeight);
      setBodyTop(body?.current?.getBoundingClientRect().top);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!toastIsOpened && clickedItem === 'to_confirm') {
      setClickedItem('');
    }
  }, [toastIsOpened]);

  useEffect(() => {
    if (!clickedItem) {
      setClickedItem('');
    }
    if (clickedItem === 'to_confirm') {
      toast.dispatch(
        'Data não disponível!',
        'warning',
        <>
          A data do crédito do seu benefício depende do RH da sua empresa. Assim
          que essa informação for confirmada pelo RH, disponibilizaremos a data
          nesse campo pra você. &#128521;
        </>,
      );
    }
    if (clickedItem === 'change_password') {
      dispatch(setCreatingPassword(false));
      setClearValues(false);
      setCurrentStep('password');
      setModalIsOpen(true);
    }
    if (clickedItem === 'where_to_use') {
      setShowMaps(true);
    }
    if (clickedItem === 'lock_card') {
      setCurrentStep('block_reasons');
      setModalIsOpen(true);
    }
    if (clickedItem === 'unlock_card') {
      setCurrentStep('unlock_card');
      setModalIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedItem]);

  function handleSetStep(stepName) {
    setModalIsOpen(false);
    if (stepName === '') {
      setClickedItem('');
    }

    setTimeout(() => {
      setCurrentStep(stepName);
      setModalIsOpen(true);
    }, 500);
  }

  function handleCloseModal() {
    dispatch(setCreatingPassword(null));

    setModalIsOpen(false);
    setClearValues(true);
    setTimeout(() => {
      setClickedItem('');
    }, 500);
  }

  function handleCloseMaps() {
    setShowMaps(false);
    setClickedItem('');
  }

  function handleModalContent() {
    let content;
    if (clickedItem === 'change_password') {
      content = (
        <ChangeCardPassword
          clearValues={clearValues}
          currentStep={currentStep}
          setStep={stepName => handleSetStep(stepName)}
        />
      );
    }

    if (clickedItem === 'lock_card') {
      content = (
        <LockCard
          updateCards={() => getCards()}
          currentStep={currentStep}
          setStep={stepName => handleSetStep(stepName)}
        />
      );
    }

    if (clickedItem === 'unlock_card') {
      content = (
        <UnlockCard
          updateCards={() => getCards()}
          currentStep={currentStep}
          setStep={stepName => handleSetStep(stepName)}
        />
      );
    }

    return content;
  }

  return (
    <>
      <Container>
        <Header />
      </Container>
      <Loader isLoading={isLoading}>
        {!href.includes('caixa') ? <ModalInactivationPortal /> : ''}
        {hasContent && (
          <>
            <CardDetails
              handleClickedItem={setClickedItem}
              activeSlide={activeSlide}
              setActiveSlide={setActiveSlide}
              cards={cards}
            />
            <Body
              minHeight={
                documentHeight -
                (bodyTop || body?.current?.getBoundingClientRect().top)
              }
              ref={body}
            >
              <Container>
                <Wrapper>
                  <SliderBullets
                    activeSlide={activeSlide}
                    slidesLength={cards.length}
                    onChange={setActiveSlide}
                  />
                </Wrapper>
                <div className="swiper-container" ref={swiperEl}>
                  <div className="swiper-wrapper">
                    {cards.map((card, i) => (
                      <div className="swiper-slide" key={i}>
                        <div
                          className={`mobile-balance-management cardinfo-step ${
                            card.statusCartao !== 3 ? 'blocked' : ''
                          }`}
                        >
                          <CardActions card={card} onClick={setClickedItem} />
                        </div>
                        {card.statusCartao === 3 && (
                          <Transactions
                            card={card}
                            isSlideActive={i === activeSlide}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </Container>
            </Body>
          </>
        )}
        {!hasContent && !isLoading && <NoCards />}
      </Loader>
      <Modal
        handleClose={() => handleCloseModal()}
        open={modalIsOpen}
        fullWidth
      >
        {handleModalContent()}
      </Modal>
      <Maps show={showMaps} handleClose={handleCloseMaps} />
      {!isLoading && <HelpButton />}
      <TermsAndPolicy />
    </>
  );
};

export default Home;
